import React from 'react';

import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import { DARK_LIGHT_RECIPE_ID, SpectrogramPoint } from '@ledium/types';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AutocompleteBordered from '../AutocompleteBordered';
import AutocompleteOptionWithChart from '../AutocompleteOptionWithChart';

interface LightRecipeItem {
    id: string;
    name: string;
    description?: string;
    spectrogram?: SpectrogramPoint[];
}

const DARK_OPTION: LightRecipeItem = {
    id: DARK_LIGHT_RECIPE_ID,
    name: i18n('dark_light_recipe_name') as string,
    description: i18n('dark_light_recipe_description') as string,
    spectrogram: []
};

type LightRecipeState = LightRecipeItem[] | LightRecipeItem;

interface SelectLightRecipeProps {
    onSelectLightRecipe?: (recipe: LightRecipeItem | null) => void;
    selectedLightRecipeId?: string | null;
    showDarkOption?: boolean;
}

export const SelectLightRecipe = ({ onSelectLightRecipe, selectedLightRecipeId, showDarkOption }: SelectLightRecipeProps) => {
    const {
        pageState: { formData },
        pageActions
    } = usePageStore();
    const [lightRecipeList, setLightRecipeList] = React.useState<LightRecipeState>([]);
    const recipeId = selectedLightRecipeId ?? (formData.lightRecipe as LightRecipeItem | null)?.id ?? '';

    React.useEffect(() => {
        if (formData.readOnly && recipeId === DARK_LIGHT_RECIPE_ID) {
            return;
        }
        lediumApi.get<LightRecipeState>(`lightRecipes/${formData.readOnly ? recipeId : ''}`).then((data) => {
            setLightRecipeList(data ?? []);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.readOnly, selectedLightRecipeId, formData.lightRecipe]);

    function getOptionLabel(option: LightRecipeItem) {
        return option.name + (option.description ? ` (${option.description})` : '');
    }

    function isOptionEqualToValue(option: LightRecipeItem, value: LightRecipeItem) {
        return option.id === value.id;
    }

    function onChange(_event: React.SyntheticEvent, lightRecipe: LightRecipeItem | null) {
        if (onSelectLightRecipe) {
            onSelectLightRecipe(lightRecipe);
        } else {
            pageActions.formData.updateFormItem({ lightRecipe });
        }
    }

    function renderInput(params: object) {
        return <TextField {...(params as React.ComponentProps<typeof TextField>)} label={i18n('select_light_recipe')} variant="outlined" />;
    }

    function renderOption({ key, ...props }: React.HTMLAttributes<HTMLLIElement> & { key: React.Key }, option: LightRecipeItem) {
        return (
            <li key={key} {...props}>
                <AutocompleteOptionWithChart option={option} spectrogramData={option.spectrogram} size="small" />
            </li>
        );
    }

    if (formData.readOnly) {
        const isDark = showDarkOption && recipeId === DARK_LIGHT_RECIPE_ID;
        const recipe = lightRecipeList as LightRecipeItem;
        let recipeName: React.ReactNode = '';
        if (isDark) {
            recipeName = `${DARK_OPTION.name} (${DARK_OPTION.description})`;
        } else if (recipe?.name) {
            recipeName = recipe.name;
            if (recipe.description) {
                recipeName = `${recipeName} (${recipe.description})`;
            }
        } else {
            recipeName = i18n('no_light_recipe_selected');
        }
        return (
            <>
                <Typography variant="subtitle2">{i18n('light_recipe')}: </Typography>
                <span>{recipeId ? recipeName : i18n('no_light_recipe_selected')}</span>
            </>
        );
    }

    const recipeArray = lightRecipeList as LightRecipeItem[];
    const options = showDarkOption ? [DARK_OPTION, ...recipeArray] : recipeArray;
    const selectedValue = options.find((i) => i.id === recipeId) ?? null;

    return (
        <AutocompleteBordered
            value={selectedValue}
            options={options}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            onChange={onChange}
            renderInput={renderInput}
            renderOption={renderOption}
        />
    );
};
